import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { Linking } from 'react-native';
import isEmpty from 'lodash/isEmpty';

import { useTheme } from '@24i/nxg-sdk-higgs';
import { View } from '@24i/nxg-sdk-quarks';
import { Loader } from '@24i/nxg-sdk-gluons';
import { PlayerUI, Player } from '@24i/player-ui-react';
import { useFeature } from '@24i/nxg-sdk-smartott-shared/src/context/AppSettingsData';
import { useRouter } from '@24i/nxg-core-router/src/NextRouter';
import { IAdBreakStartedEvent, IPlayerHtmlEngine, PlayerBase } from '@24i/player-base';
import { overridable } from '@24i/nxg-sdk-gluons/src/context/ComponentOverrides';
import useBlockedModal from '@24i/nxg-sdk-smartott/src/components/BlockedModal/hooks/useBlockedModal';
import { ASSET_TYPE } from '@24i/nxg-sdk-photon/src/enums';
import {
    SOTT_DEFAULT_SCREENS,
    SOTT_DEFAULT_WEB_SCREENS,
} from '@24i/nxg-sdk-smartott/src/navigation/constants';
import {
    onAnalytics as originalAnalytics,
    ANALYTICS_TRIGGERS,
} from '@24i/nxg-sdk-smartott-shared/src/analytics';
import { useSessionId } from '@24i/nxg-sdk-smartott-shared/src/analytics/hooks/useSessionId';
import { useCampaignAnalytics } from '@24i/nxg-sdk-smartott-shared/src/analytics/hooks/useCampaign';
import { useEnrichedAnalytics } from '@24i/nxg-sdk-smartott-shared/src/analytics/hooks/useEnrichedAnalytics';
import { useModal } from '@24i/nxg-sdk-gluons/src/context/Modal';
import { isWebView, getTelegramWebApp } from '@24i/nxg-sdk-higgs/src/utils';

import { useBackNavigationDebounce } from '../hooks/useBackNavigationDebounce';
import { handlePlaybackError } from '../helpers';
import getPlaybackScreenStyles from '../styles';
import { useEpgDataManager } from '../managers';
import { IPlaybackScreen } from '../types/index';
import withErrorModal from '../../../components/GenericModal/hoc/ErrorModal';
import { useAppNavigation } from '../../../context/AppNavigation';
import TelegramDRMWorkaround from '../modals/TelegramDRMWorkaround';

const PlaybackScreen: FunctionComponent<IPlaybackScreen> = ({
    styles = getPlaybackScreenStyles,
    onError,
    asset,
    seriesData,
    setVideoPlayerRef,
    onPlay,
    onEnd,
    onStop,
    onSeeking,
    onSeeked,
    onDurationChange,
    onPause,
    onAdBreakEnd,
    onAdBreakStart,
    onBitrateChange,
    onUnlockAction,
    onCurrentProgramChange,
    engine,
    source,
    startPosition,
    startPositionType,
    useDurationOverRemaining = true,
    blockingReason,
    blockingMessage,
    related,
    openErrorModal,
    errorMessage,
    dvrWindowOverride,
    shouldShowAirtime,
    isCheckingBlockers,
    onGoToNextEpisode,
    ...playerProps
}) => {
    const router = useRouter();
    const epgData = useEpgDataManager(asset);
    const { theme } = useTheme();
    const { previousRoute } = useAppNavigation();
    const themedStyles = styles(theme);
    const { openBlockedModal } = useBlockedModal();
    const { sessionId } = useSessionId();
    const { campaignUrl, campaignCookie } = useCampaignAnalytics();
    const onAnalytics = useEnrichedAnalytics(originalAnalytics);
    const [blocker, setBlocker] = useState(blockingReason);
    const deepLinking = useFeature('deepLinking');
    const { setModalChildren, closeModal, setModalProps } = useModal();
    const isPlayerOpened = useRef(false);
    const [playerRef, setPlayerRef] = useState<PlayerBase | null>(null);
    const isAdBreakSceduled = useRef<IAdBreakStartedEvent | null>(null);
    const isPostRollPlaying = useRef<boolean>(false);
    const finalSetVideoPlayerRef = (player: PlayerBase) => {
        setPlayerRef(player);
        setVideoPlayerRef?.(player);
    };

    const deepLinkingEnabled = deepLinking?.enabled && deepLinking.storeUrls;
    const appStoreUrl = deepLinking?.storeUrls?.ios;
    const playStoreUrl = deepLinking?.storeUrls?.android;
    const { userAgent } = navigator;

    const isIOS = /iPhone|iPad|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);

    useEffect(() => {
        if (deepLinkingEnabled && document.referrer === '') {
            if (isAndroid && playStoreUrl) {
                Linking.openURL(playStoreUrl);
            } else if (isIOS && appStoreUrl) {
                Linking.openURL(appStoreUrl);
            }
        }
    }, []);

    const onPlayerEndHandler = useCallback(() => {
        setTimeout(() => {
            if (isAdBreakSceduled.current !== null) {
                // POST-Roll playback: no action needed yet
                isPostRollPlaying.current = true;
            } else {
                if (onEnd) onEnd();
                if (
                    asset &&
                    (asset.isTrailer || !onGoToNextEpisode || !onGoToNextEpisode(asset, seriesData))
                ) {
                    router.back();
                }
            }
        }, 200);
    }, [asset, onEnd, onGoToNextEpisode, seriesData, router]);

    const handleAdBreakStart = (ad: IAdBreakStartedEvent) => {
        isAdBreakSceduled.current = ad;
    };

    const handleAdBreakEnd = () => {
        isAdBreakSceduled.current = null;
        if (isPostRollPlaying.current) {
            // Close player if it's a post-roll adEnd
            isPostRollPlaying.current = false;
            onPlayerEndHandler();
        }
    };

    useEffect(() => {
        if (playerRef && typeof playerRef.addEventListener === 'function') {
            playerRef.addEventListener('ended', onPlayerEndHandler);
            playerRef.addEventListener('adbreakstarted', handleAdBreakStart);
            playerRef.addEventListener('adbreakended', handleAdBreakEnd);
        }

        return () => {
            if (playerRef && typeof playerRef.removeEventListener === 'function') {
                playerRef.removeEventListener('ended', onPlayerEndHandler);
                playerRef.removeEventListener('adbreakstarted', handleAdBreakStart);
                playerRef.removeEventListener('adbreakended', handleAdBreakEnd);
            }
        };
    }, [playerRef]);

    useEffect(() => {
        setBlocker(blockingReason);
    }, [blockingReason]);

    useEffect(
        () => () => {
            // send stop event when the user leaves the playback screen
            // this seems is not supported by player itself for web
            if (onEnd) onEnd();
        },
        []
    );

    useEffect(() => {
        if (asset) {
            onAnalytics(ANALYTICS_TRIGGERS.SCENE_VIEW, {
                asset,
                screen: SOTT_DEFAULT_SCREENS.PLAYBACK,
                sessionId,
                campaignUrl,
                campaignCookie,
            });
        }

        if (asset && !!seriesData.series?.title) {
            const finalAsset = {
                ...asset,
                seriesTitle: seriesData.series?.title,
                // parameter that allow to check and send to Analytics the same payload when user open the same
                // episode several times for [PLAYER_OPEN and PLAYBACK_START] events
                analyticsRandomVal: Math.random(), // this value WILL NOT send to Analytics
            };
            if (!isPlayerOpened.current) {
                onAnalytics(ANALYTICS_TRIGGERS.PLAYER_OPEN, {
                    asset: finalAsset,
                    sessionId,
                    campaignUrl,
                    campaignCookie,
                });
                isPlayerOpened.current = true;
            }
        }
    }, [asset, seriesData.series?.title, source]);

    const handleNavigation = useCallback(async () => {
        if (onStop) await onStop();

        if (previousRoute?.includes('details')) {
            router.back();
        } else {
            const homeLink = SOTT_DEFAULT_WEB_SCREENS.Home.getLink();
            router.push(homeLink.href, homeLink.as);
        }
    }, [onStop, previousRoute, router]);

    const { handleBack: onBackHandler } = useBackNavigationDebounce(handleNavigation);

    const onEndHandler = useCallback(() => {
        /** The onEnd callback is triggered twice on web — once for content playback end and once for ad playback end.
          To handle this, we’ve added a direct listener to the playerRef’s 'ended' event and manage the logic based 
          on ad state within the 'onPlayerEndHandler' function. 
        */
    }, [onEnd, asset, seriesData]);

    const onClose = () => {
        router.back();
    };

    const playerSource = source || null;
    useEffect(() => {
        if (playerSource && playerSource.drm && isWebView() && isAndroid) {
            // ignore drm error
        } else {
            handlePlaybackError(
                errorMessage,
                openBlockedModal,
                setBlocker,
                openErrorModal,
                onClose
            );
        }
    }, [errorMessage?.title, errorMessage?.body, playerSource]);

    const isLiveBroadcast: boolean =
        !!asset?.isLive &&
        [ASSET_TYPE.CHANNEL, ASSET_TYPE.EPG, ASSET_TYPE.BROADCAST].includes(asset.type);
    const shouldShowFooterPlayerButtons = !blocker || isLiveBroadcast;

    const avoidPlayerOnAndroidWebview =
        playerSource && playerSource.drm && !blocker && isWebView() && isAndroid;

    useEffect(() => {
        if (avoidPlayerOnAndroidWebview) {
            setModalProps({
                onModalClose: onBackHandler,
            });
            setModalChildren(
                <TelegramDRMWorkaround closeModal={closeModal} onBackHandler={onBackHandler} />
            );
        }
    }, [playerSource, blocker]);

    // Detect Telegram WebApp and whether to offset header
    const TelegramWebApp = getTelegramWebApp && getTelegramWebApp();
    // Ensure headerOffset is always a number or undefined
    const headerOffset =
        TelegramWebApp &&
        TelegramWebApp.initDataUnsafe &&
        typeof TelegramWebApp.headerHeight === 'number'
            ? TelegramWebApp.headerHeight
            : undefined;

    if (isEmpty(asset)) {
        return (
            <Loader
                color={theme.color?.textPrimary || 'white'}
                size="large"
                disableImageBackground
                additionalStyles={themedStyles?.holder}
            />
        );
    }

    return (
        <>
            <View
                style={{
                    ...themedStyles?.holder,
                    userSelect: 'none',
                }}
            >
                {(playerSource || blocker) && engine && !avoidPlayerOnAndroidWebview ? (
                    <Player
                        engine={engine as IPlayerHtmlEngine}
                        onError={onError}
                        onEnd={onEndHandler}
                        hasBlockingReason={!!blocker}
                        {...playerProps}
                        {...{
                            source: playerSource,
                            onPlay,
                            onSeeking,
                            onSeeked,
                            onDurationChange,
                            onPause,
                            onStop,
                            onAdBreakEnd,
                            onAdBreakStart,
                            setVideoPlayerRef: finalSetVideoPlayerRef,
                            startPosition,
                            startPositionType,
                            onBitrateChange,
                            isCheckingBlockers,
                        }}
                    >
                        {(playerInstance) => (
                            <PlayerUI
                                {...{
                                    playerInstance,
                                    asset,
                                    seriesData,
                                    epgData,
                                    useDurationOverRemaining,
                                    dvrWindowOverride,
                                    shouldShowFooterPlayerButtons,
                                    shouldShowAirtime,
                                    onUnlockAction,
                                    onCurrentProgramChange,
                                    playerRef,
                                }}
                                shouldShowBufferedRanges={false}
                                blockingReason={blocker}
                                blockingMessage={blockingMessage}
                                onBack={onBackHandler}
                                onGoToNextEpisode={onGoToNextEpisode}
                                relatedItems={related}
                                headerOffset={headerOffset}
                            />
                        )}
                    </Player>
                ) : (
                    <Loader
                        color={theme.color?.textPrimary || 'white'}
                        size="large"
                        disableImageBackground
                        additionalStyles={themedStyles?.holder}
                    />
                )}
            </View>
        </>
    );
};

export default withErrorModal(overridable(PlaybackScreen, 'PlaybackScreen'));
